/**
 * The "Tasks" section of a record slideout — the tasks linked to this
 * contact / company / deal / meeting. A task is a case, so it reaches the
 * record through the case-kind rules every case has (`ahoy_deal_to_contact`,
 * …); this section lists `ahoy_task` objects by parent record and does not
 * care which rule carried the link. (The overview's inherited row still wears
 * the deal-flavoured label until #12411 gives labels per entity-type pair.)
 *
 * Shown when the record's entity type carries a live ahoy_task association
 * (RecordViewContext's `tasksEnabled`, derived purely from the org's schema —
 * `hasTaskAssociation` and the resolved sections config). That render gate is
 * broad: it includes hosts reachable only through an internal transitive rule
 * (a meeting via contact — AC-10), so a meeting still lists its derived tasks.
 * The "Add task" affordance uses the STRICT `useHasWritableTaskAssociation`
 * gate instead — the composer can only write a user-writable host link.
 *
 * Each task renders as a TaskCard: title, description, assignee, status and
 * due date all edit in place, saved as CRM events through useMemoRecords run
 * under the task family.
 */

import { Plus } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useCurrentUserEntityId, useOrgMembers } from '@/hooks/useOrgMembers';
import { usePermissions } from '@/hooks/usePermissions';
import { QueryErrorState } from '@/lib/components';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import {
  type PropertyTypeSchema,
  usePropertySchema,
} from '@/lib/crm/hooks/usePropertySchema';

import { memoDueValue } from '../memo/notes.utils';
import { type Note, useMemoRecords } from '../memo/useMemoRecords';
import {
  TASK_ENTITY_TYPE,
  TASK_OWNER_ASSOCIATION_TYPE_NAME,
} from '../task/task.constants';
import { TaskCard } from '../task/TaskCard';
import { useHasWritableTaskAssociation } from '../task/useHasTaskAssociation';
import { useTaskTabActions } from '../task/useTaskTabActions';
import { CollapsibleSection } from './collapsible-section';
import { SectionActionButton } from './section-action-button';

type MemoRecords = ReturnType<typeof useMemoRecords>;

interface TasksSectionProps {
  orgId: number;
  entityId: string;
  entityTypeName: string;
}

export function TasksSection({
  orgId,
  entityId,
  entityTypeName,
}: TasksSectionProps) {
  const { hasPermission } = usePermissions();
  const canDeleteTask = hasPermission(TASK_ENTITY_TYPE, 'ahoy_object_deleted');
  // Property writes (title, description, due date) and association writes
  // (assignee, status) are independently grantable — gate each on the event
  // its save submits.
  const canEditProperties = hasPermission(
    TASK_ENTITY_TYPE,
    'ahoy_property_updated'
  );
  // Association adds and removes are separately grantable. Reassigning or
  // clearing an existing owner emits BOTH events, and a description edit that
  // changes @mentions emits whichever side of the mention diff applies — so
  // each side is gated on its own grant rather than assuming one implies the
  // other.
  const canAddAssociations = hasPermission(
    TASK_ENTITY_TYPE,
    'ahoy_association_added'
  );
  const canRemoveAssociations = hasPermission(
    TASK_ENTITY_TYPE,
    'ahoy_association_removed'
  );

  const { user } = useAuthInfo();
  const userId = user?.id ? String(user.id) : undefined;
  const { members: orgUsers } = useOrgMembers(orgId);
  const userEntityId = useCurrentUserEntityId(orgId);

  const { schema } = usePropertySchema(orgId, TASK_ENTITY_TYPE);
  const { associationTypes } = useAssociationTypes(orgId);
  const assigneeAssociationType = useMemo(
    () =>
      associationTypes.find(
        at => at.association_type_name === TASK_OWNER_ASSOCIATION_TYPE_NAME
      ),
    [associationTypes]
  );

  const {
    records: tasks,
    isLoading,
    error: fetchError,
    refetch,
    updateRecordAsync,
    updateRecordStageAsync,
    deleteRecord,
    isDeleting,
    deleteError,
  } = useMemoRecords({
    orgId,
    entityId,
    entityTypeName,
    users: orgUsers,
    recordType: TASK_ENTITY_TYPE,
  });

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  // "Add task" opens the shared composer pre-linked to this record with the
  // default task stage selected — the same hook the record's Tasks tab uses,
  // so both entry points open identically.
  const { startCreatingTask, canCreateTask, isStageLoading } =
    useTaskTabActions(entityId, entityTypeName);
  // The section RENDERS for any host with a live task association (including a
  // meeting reachable only via an internal transitive rule — AC-10), but the
  // composer can only write a USER-WRITABLE host link. Gate "Add task" on the
  // strict predicate so a meeting lists its derived tasks without offering a
  // create the schema would drop on submit.
  const hasWritableTaskAssociation = useHasWritableTaskAssociation(
    orgId,
    entityTypeName
  );

  // Track which task a delete targets so the shared mutation state only
  // surfaces on that card.
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);

  const actionButtons = useMemo(
    () =>
      canCreateTask && userEntityId && hasWritableTaskAssociation ? (
        <SectionActionButton
          label='Add task'
          onClick={startCreatingTask}
          icon={Plus}
          disabled={isStageLoading}
          disabledTooltip='Loading…'
        />
      ) : undefined,
    [
      canCreateTask,
      userEntityId,
      hasWritableTaskAssociation,
      startCreatingTask,
      isStageLoading,
    ]
  );

  return (
    <CollapsibleSection title='Tasks' actions={actionButtons}>
      {isLoading ? (
        <div
          className='h-16 rounded-lg bg-[var(--color-bg-tertiary)] animate-pulse'
          aria-hidden='true'
        />
      ) : (
        <div className='space-y-4'>
          {fetchError && (
            <QueryErrorState
              error={fetchError}
              label="We couldn't load tasks"
              onRetry={handleRetry}
              size='sm'
            />
          )}

          {!fetchError && tasks.length === 0 && (
            <p className='text-xs md:text-sm text-fg-secondary'>No tasks yet</p>
          )}

          {!fetchError && tasks.length > 0 && (
            <div className='space-y-4'>
              {tasks.map((task: Note) => (
                <div
                  key={task.entity_id}
                  data-focused-association-id={task.entity_id}
                >
                  <TaskListItem
                    task={task}
                    orgId={orgId}
                    userId={userId}
                    dueDateSchema={schema['ahoy_date']}
                    assigneeAssociationType={assigneeAssociationType}
                    canEditProperties={canEditProperties}
                    canAddAssociations={canAddAssociations}
                    canRemoveAssociations={canRemoveAssociations}
                    canDelete={canDeleteTask}
                    updateRecordAsync={updateRecordAsync}
                    updateRecordStageAsync={updateRecordStageAsync}
                    deleteRecord={deleteRecord}
                    onDeleteStart={setDeletingTaskId}
                    isDeleting={isDeleting && deletingTaskId === task.entity_id}
                    deleteError={
                      deletingTaskId === task.entity_id &&
                      deleteError instanceof Error
                        ? deleteError
                        : null
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
}

interface TaskListItemProps {
  task: Note;
  orgId: number;
  userId: string | undefined;
  dueDateSchema: PropertyTypeSchema | undefined;
  assigneeAssociationType: AssociationType | undefined;
  canEditProperties: boolean;
  canAddAssociations: boolean;
  canRemoveAssociations: boolean;
  canDelete: boolean;
  updateRecordAsync: MemoRecords['updateRecordAsync'];
  updateRecordStageAsync: MemoRecords['updateRecordStageAsync'];
  deleteRecord: MemoRecords['deleteRecord'];
  onDeleteStart: (taskEntityId: string) => void;
  isDeleting: boolean;
  deleteError: Error | null;
}

/**
 * One task in the list. Its own component so each card's save handlers are
 * stable references bound to that task, rather than closures rebuilt for
 * every task on every render of the list.
 */
function TaskListItem({
  task,
  orgId,
  userId,
  dueDateSchema,
  assigneeAssociationType,
  canEditProperties,
  canAddAssociations,
  canRemoveAssociations,
  canDelete,
  updateRecordAsync,
  updateRecordStageAsync,
  deleteRecord,
  onDeleteStart,
  isDeleting,
  deleteError,
}: TaskListItemProps) {
  // One field at a time: every other value is re-submitted unchanged, and the
  // update mutation diffs against the originals, so only the edited field is
  // written. Rejections propagate so the editor that saved shows the error.
  const saveFields = useCallback(
    async (fields: {
      title?: string;
      content?: string;
      date?: string | null;
      ownerEntityId?: string | null;
    }) => {
      if (!userId) {
        throw new Error("We couldn't save that. Try again.");
      }
      await updateRecordAsync({
        noteEntityId: task.entity_id,
        title: fields.title ?? task.title,
        content: fields.content ?? task.content,
        date:
          fields.date === undefined
            ? memoDueValue(task)
            : (fields.date ?? undefined),
        ownerEntityId:
          fields.ownerEntityId === undefined
            ? (task.owner_entity_id ?? undefined)
            : (fields.ownerEntityId ?? undefined),
        originalTitle: task.title,
        originalContent: task.content,
        originalDate: task.date,
        originalDatetime: task.datetime,
        originalOwnerEntityId: task.owner_entity_id,
        stageAssociation: task.stage_entity_id ?? null,
        originalStageAssociation: task.stage_entity_id ?? null,
        lastEventSeq: task.last_event_seq,
        userId,
        // A description edit may add or remove @mention associations; emit each
        // side only when the user holds that grant, so a content-only save is
        // never rejected for a mention association write they can't make.
        canAddMentions: canAddAssociations,
        canRemoveMentions: canRemoveAssociations,
      });
    },
    [task, updateRecordAsync, userId, canAddAssociations, canRemoveAssociations]
  );

  const handleSaveTitle = useCallback(
    (title: string) => saveFields({ title }),
    [saveFields]
  );
  const handleSaveDescription = useCallback(
    (content: string) => saveFields({ content }),
    [saveFields]
  );
  const handleSaveDue = useCallback(
    (date: string | null) => saveFields({ date }),
    [saveFields]
  );
  const handleSaveAssignee = useCallback(
    (targetIds: string[]) =>
      saveFields({ ownerEntityId: targetIds[0] ?? null }),
    [saveFields]
  );

  const handleStageChange = useCallback(
    async (_taskEntityId: string, newStageEntityId: string | null) => {
      if (!userId) {
        throw new Error('Cannot change task stage: no authenticated user');
      }
      await updateRecordStageAsync(task, newStageEntityId, userId);
    },
    [task, updateRecordStageAsync, userId]
  );

  // Not caught on purpose: TaskCard's confirmation needs the rejection to stay
  // open and render `deleteError`.
  const handleDelete = useCallback(
    async (taskEntityId: string) => {
      if (!userId) {
        return;
      }
      onDeleteStart(taskEntityId);
      await deleteRecord({
        noteEntityId: taskEntityId,
        lastEventSeq: task.last_event_seq ?? 0,
        userId,
      });
    },
    [deleteRecord, onDeleteStart, task.last_event_seq, userId]
  );

  const canSaveProperties = canEditProperties && userId !== undefined;
  // Reassigning or clearing an EXISTING owner emits an `ahoy_association_removed`
  // alongside the add, so that path needs BOTH grants — otherwise a user with
  // add-but-not-remove sees an editable row whose every save fails. But an
  // ownerless task's first assignment is add-only (there is nothing to remove),
  // so it needs only the add grant; requiring remove there would wrongly hide
  // the forward-path editor from an add-only user.
  const taskHasOwner = task.owner_entity_id != null;
  const canEditAssignee =
    canAddAssociations &&
    (!taskHasOwner || canRemoveAssociations) &&
    userId !== undefined;
  const canEditStage = canAddAssociations && userId !== undefined;

  return (
    <TaskCard
      task={task}
      orgId={orgId}
      stageEntityId={task.stage_entity_id ?? null}
      ownerEntityId={task.owner_entity_id}
      dueDateSchema={dueDateSchema}
      assigneeAssociationType={assigneeAssociationType}
      onSaveTitle={canSaveProperties ? handleSaveTitle : undefined}
      onSaveDescription={canSaveProperties ? handleSaveDescription : undefined}
      onSaveDue={canSaveProperties ? handleSaveDue : undefined}
      onSaveAssignee={canEditAssignee ? handleSaveAssignee : undefined}
      onStageChange={canEditStage ? handleStageChange : undefined}
      // `handleDelete` no-ops without a userId, so withhold the affordance
      // until one exists — otherwise the confirmation closes as if it deleted.
      onDelete={canDelete && userId !== undefined ? handleDelete : undefined}
      isDeleting={isDeleting}
      deleteError={deleteError}
    />
  );
}
